import React, { useState, useCallback } from 'react';
import {
  TouchableOpacity,
  TextInput as RNTextInput,
  Image,
  Platform,
} from 'react-native';
import type { TextStyle, ViewStyle } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Text, useTheme } from '@tamagui/core';
import { YStack, XStack } from '@tamagui/stacks';

import type { RootStackScreenProps } from '@/types/navigation';
import { SvgBackground } from '@/components';
import { Eye, EyeOff, Apple, Facebook, Chrome } from 'lucide-react-native';
import { COLORS, COMMON_STYLES } from '@/constants';
import { useLogin } from '@/api';
import { useAuth } from '@/contexts/AuthContext';
import { showErrorToast } from '@/utils/toast';
import { logo } from '@/assets';

type Props = RootStackScreenProps<'Auth'>;

const AuthScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { setToken } = useAuth();
  const isWeb = Platform.OS === 'web';

  const [activeTab] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const login = useLogin({
    onSuccess: (data) => {
      setLoginError(null);
      setToken(data.token);
    },
    onError: (error: any) => {
      // Check for invalid credentials error (code can be number or string)
      const isInvalidCredentials = 
        error.message === 'Invalid credentials.' || 
        error.code === 401 || 
        error.code === '401';
      
      const errorMessage = isInvalidCredentials
        ? t('screens.auth.invalidCredentials', 'Nieprawidłowy email lub hasło')
        : error.message || t('screens.auth.loginError', 'Logowanie nie powiodło się. Spróbuj ponownie.');
      
      setLoginError(errorMessage);
    },
  });

  const handleLogin = useCallback(() => {
    if (!email.trim() || !password.trim()) {
      showErrorToast(
        t('common.error', 'Error'),
        t('screens.auth.fillFields', 'Please fill in all fields')
      );
      return;
    }

    login.mutate({ username: email.trim(), password });
  }, [email, password, t, login]);

  const handleRegister = useCallback(() => {
    navigation.navigate('Register');
  }, [navigation]);

  const handleTabChange = useCallback((tab: 'login' | 'register') => {
    if (tab === 'register') {
      navigation.navigate('Register');
    }
  }, [navigation]);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const isFormValid = email.trim().length > 0 && password.length > 0;
  const isSubmitting = login.isPending;

  // Web layout
  if (isWeb) {
    return (
      <YStack flex={1} backgroundColor="$background" paddingHorizontal="$5" paddingVertical="$5" justifyContent="space-between">
        {/* Main content */}
        <YStack>
        {/* Title */}
        <YStack alignItems="center" marginBottom="$4">
          <Text fontSize={26} fontWeight="500" color="$color" textAlign="center">
            Witaj z powrotem!
          </Text>
          <Text fontSize={13} color="$color10" textAlign="center" marginTop={8} paddingHorizontal="$2">
            Zaloguj się aby kontynuować znajdowanie nowych znajomości
          </Text>
        </YStack>

        {/* Tab Switcher */}
        <XStack 
          marginBottom="$6"
          borderRadius={10}
          padding={4}
          borderWidth={1}
          borderColor="#e8e4e0"
          backgroundColor="#f8f7f6"
        >
          <TouchableOpacity
            onPress={() => handleTabChange('login')}
            style={getWebTabStyle(activeTab === 'login')}
          >
            <Text 
              color={activeTab === 'login' ? COLORS.PRIMARY : '$color10'} 
              fontSize={13}
              fontWeight={activeTab === 'login' ? '600' : '400'}
            >
              {t('screens.auth.loginTab', 'Logowanie')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => handleTabChange('register')}
            style={getWebTabStyle(activeTab === 'register')}
          >
            <Text 
              color={activeTab === 'register' ? COLORS.PRIMARY : '$color10'} 
              fontSize={13}
              fontWeight={activeTab === 'register' ? '600' : '400'}
            >
              {t('screens.auth.registerTab', 'Rejestracja')}
            </Text>
          </TouchableOpacity>
        </XStack>

        {/* Form */}
        <YStack marginBottom="$2">
          <RNTextInput
            value={email}
            onChangeText={setEmail}
            keyboardType="email-address"
            autoCapitalize="none"
            placeholder={t('common.emailNickname', 'Email/nickname')}
            placeholderTextColor={theme.color10?.val}
            style={getWebInputStyle(theme)}
          />

          <XStack position="relative" marginTop={12}>
            <RNTextInput
              value={password}
              onChangeText={setPassword}
              secureTextEntry={!showPassword}
              placeholder={t('common.password', 'Hasło')}
              placeholderTextColor={theme.color10?.val}
              style={getWebPasswordInputStyle(theme)}
            />
            <TouchableOpacity
              onPress={togglePasswordVisibility}
              style={getWebEyeButtonStyle()}
            >
              {showPassword ? (
                <EyeOff size={18} color={theme.color11?.val} />
              ) : (
                <Eye size={18} color={theme.color11?.val} />
              )}
            </TouchableOpacity>
          </XStack>
        </YStack>

        <XStack justifyContent="flex-end" marginBottom="$4">
          <TouchableOpacity onPress={() => {}}>
            <Text color={COLORS.PRIMARY} fontSize={11}>
              {t('common.forgotPassword', 'Zapomniałeś hasła?')}
            </Text>
          </TouchableOpacity>
        </XStack>

        {loginError && (
          <Text color="#ef4444" fontSize={13} textAlign="center" marginBottom="$2">
            {loginError}
          </Text>
        )}

        <TouchableOpacity
          activeOpacity={0.9}
          disabled={!isFormValid || isSubmitting}
          onPress={handleLogin}
          style={getWebButtonStyle(!isFormValid || isSubmitting)}
        >
          <Text color="white" fontSize={14} fontWeight="600">
            {isSubmitting
              ? t('common.loading', 'Ładowanie...')
              : t('common.signIn', 'Zaloguj się')}
          </Text>
        </TouchableOpacity>

        <XStack justifyContent="center" marginTop={16}>
          <Text color="$color10" fontSize={11}>
            {t('screens.auth.noAccount', 'Nie masz konta?')}{' '}
          </Text>
          <TouchableOpacity onPress={handleRegister}>
            <Text color={COLORS.PRIMARY} fontSize={11} fontWeight="500">
              {t('screens.auth.registerLink', 'Zarejestruj się')}
            </Text>
          </TouchableOpacity>
        </XStack>
        </YStack>

        {/* Social Login - at bottom */}
        <YStack alignItems="center">
          <Text color={COLORS.PRIMARY} fontSize={11} marginBottom={12}>
            {t('common.signInWith', 'Zaloguj się za pomocą')}
          </Text>
          <XStack space="$3" justifyContent="center">
            <TouchableOpacity style={getWebSocialButtonStyle(theme)}>
              <Chrome size={20} color={theme.color?.val} />
            </TouchableOpacity>
            <TouchableOpacity style={getWebSocialButtonStyle(theme)}>
              <Facebook size={20} color={theme.color?.val} />
            </TouchableOpacity>
            <TouchableOpacity style={getWebSocialButtonStyle(theme)}>
              <Apple size={20} color={theme.color?.val} />
            </TouchableOpacity>
          </XStack>
        </YStack>
      </YStack>
    );
  }

  // Mobile layout - original design
  return (
    <YStack flex={1} backgroundColor="$background">
      {/* Header */}
      <XStack
        paddingTop="$8"
        paddingHorizontal="$4"
        paddingBottom="$3"
        justifyContent="space-between"
        alignItems="center"
      >
        <XStack space="$3" />
      </XStack>

      {/* Hero Section */}
      <YStack>
        <SvgBackground height={220}>
          <Image
            source={logo}
            style={COMMON_STYLES.logo}
          />

          <Text
            fontSize="$8"
            fontWeight="bold"
            color="white"
            textAlign="center"
            fontFamily="Poppins"
          >
            {t('screens.auth.title', 'Witamy ponownie')}
          </Text>

          <Text
            fontSize="$6"
            color="white"
            opacity={0.9}
            textAlign="center"
            fontFamily="Poppins"
            maxWidth="80%"
          >
            {t('screens.auth.subtitle', 'Zaloguj się do swojego konta')}
          </Text>
        </SvgBackground>
      </YStack>

      {/* Form */}
      <YStack paddingHorizontal="$4" paddingTop="$4" space="$4" zIndex={2}>
        <RNTextInput
          value={email}
          onChangeText={setEmail}
          keyboardType="email-address"
          autoCapitalize="none"
          placeholder={t('common.emailPlaceholder', 'Email')}
          placeholderTextColor={theme.color10?.val}
          style={getInputStyle(theme)}
        />

        <XStack position="relative">
          <RNTextInput
            value={password}
            onChangeText={setPassword}
            secureTextEntry={!showPassword}
            placeholder={t('common.passwordPlaceholder', 'Hasło')}
            placeholderTextColor={theme.color10?.val}
            style={getPasswordInputStyle(theme)}
          />
          <TouchableOpacity
            onPress={togglePasswordVisibility}
            style={getEyeButtonStyle()}
          >
            {showPassword ? (
              <EyeOff size={20} color={theme.color11?.val} />
            ) : (
              <Eye size={20} color={theme.color11?.val} />
            )}
          </TouchableOpacity>
        </XStack>

        <XStack justifyContent="flex-end">
          <TouchableOpacity onPress={() => {}}>
            <Text color={COLORS.PRIMARY} fontSize="$3">
              {t('common.forgotPassword', 'Zapomniałeś hasła?')}
            </Text>
          </TouchableOpacity>
        </XStack>

        {loginError && (
          <Text color="#ef4444" fontSize="$3" textAlign="center">
            {loginError}
          </Text>
        )}

        <TouchableOpacity
          activeOpacity={0.9}
          disabled={!isFormValid || isSubmitting}
          onPress={handleLogin}
          style={getLoginButtonStyle(theme, !isFormValid || isSubmitting)}
        >
          <Text color="white" fontSize="$4" fontWeight="600">
            {isSubmitting
              ? t('common.loading', 'Ładowanie...')
              : t('common.signIn', 'Zaloguj się')}
          </Text>
        </TouchableOpacity>

        <XStack justifyContent="center" space="$1">
          <Text color="$color10" fontSize="$3">
            {t('screens.auth.noAccount', 'Nie masz konta?')}
          </Text>
          <TouchableOpacity onPress={handleRegister}>
            <Text color={COLORS.PRIMARY} fontSize="$3" fontWeight="500">
              {t('screens.auth.registerLink', 'Zarejestruj się')}
            </Text>
          </TouchableOpacity>
        </XStack>

        <YStack
          alignItems="center"
          space="$3"
          paddingTop="$3"
          paddingBottom="$6"
        >
          <Text color={COLORS.PRIMARY} fontSize="$3">
            {t('common.signInWith', 'Zaloguj się za pomocą')}
          </Text>
          <XStack space="$4" justifyContent="center">
            <TouchableOpacity style={getSocialButtonStyle(theme)}>
              <Apple size={24} color={theme.color?.val} />
            </TouchableOpacity>
            <TouchableOpacity style={getSocialButtonStyle(theme)}>
              <Facebook size={24} color={theme.color?.val} />
            </TouchableOpacity>
            <TouchableOpacity style={getSocialButtonStyle(theme)}>
              <Chrome size={24} color={theme.color?.val} />
            </TouchableOpacity>
          </XStack>
        </YStack>
      </YStack>
    </YStack>
  );
};

// Web styles
function getWebTabStyle(isActive: boolean): ViewStyle {
  return {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: isActive ? '#ffffff' : 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
  };
}

function getWebInputStyle(theme: ReturnType<typeof useTheme>): TextStyle {
  return {
    height: 46,
    borderWidth: 1,
    borderColor: theme.borderColor?.val ?? '#e5e7eb',
    borderRadius: 10,
    paddingHorizontal: 16,
    fontSize: 13,
    color: theme.color?.val,
    backgroundColor: theme.background?.val,
    fontFamily: 'Poppins',
  };
}

function getWebPasswordInputStyle(theme: ReturnType<typeof useTheme>): TextStyle {
  return {
    ...getWebInputStyle(theme),
    flex: 1,
    paddingRight: 48,
  };
}

function getWebEyeButtonStyle(): ViewStyle {
  return {
    position: 'absolute',
    right: 14,
    top: 14,
    padding: 2,
  };
}

function getWebButtonStyle(disabled: boolean): ViewStyle {
  return {
    height: 46,
    borderRadius: 10,
    backgroundColor: disabled ? COLORS.DISABLED : COLORS.PRIMARY,
    alignItems: 'center',
    justifyContent: 'center',
  };
}

function getWebSocialButtonStyle(theme: ReturnType<typeof useTheme>): ViewStyle {
  return {
    width: 42,
    height: 42,
    borderRadius: 21,
    borderWidth: 1,
    borderColor: theme.borderColor?.val ?? '#e5e7eb',
    backgroundColor: theme.background?.val,
    alignItems: 'center',
    justifyContent: 'center',
  };
}

// Mobile styles
function getInputStyle(theme: ReturnType<typeof useTheme>): TextStyle {
  return {
    ...COMMON_STYLES.input,
    borderColor: theme.borderColor?.val ?? '#e5e7eb',
    color: theme.color?.val,
    backgroundColor: theme.background?.val,
  };
}

function getPasswordInputStyle(theme: ReturnType<typeof useTheme>): TextStyle {
  return {
    ...getInputStyle(theme),
    flex: 1,
    paddingRight: 50,
  };
}

function getEyeButtonStyle(): ViewStyle {
  return {
    position: 'absolute',
    right: 16,
    top: 16,
    padding: 4,
  };
}

function getLoginButtonStyle(
  theme: ReturnType<typeof useTheme>,
  disabled: boolean,
): ViewStyle {
  return {
    ...COMMON_STYLES.button,
    backgroundColor: disabled ? COLORS.DISABLED : COLORS.PRIMARY,
    width: '100%',
  };
}

function getSocialButtonStyle(theme: ReturnType<typeof useTheme>): ViewStyle {
  return {
    ...COMMON_STYLES.socialButton,
    borderColor: theme.borderColor?.val ?? '#e5e7eb',
    backgroundColor: theme.background?.val,
  };
}

export default AuthScreen;
